/** @prettier */

import {
  Grid,
  Box,
  Container,
  Button,
  Stack,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  ListItemButton,
  ListItemIcon,
  Checkbox,
  ToggleButtonGroup,
  ToggleButton,
  Slider,
  InputAdornment,
  Paper,
  Switch,
} from '@mui/material';
import {utcToZonedTime} from 'date-fns-tz';
import CalculateIcon from '@mui/icons-material/Calculate';
import {useState, useEffect, Fragment} from 'react';
import {
  useLoaderData,
  useParams,
  LoaderFunctionArgs,
  useNavigate,
} from 'react-router-dom';

//MTH component import
import {ChkInputTextbox} from '../../component/ChkInputTextbox';
import DialogPage from '../../component/DialogMessage';
//API import
import {
  getCompositionsGateway,
  getSettingSavingEnergy,
  getCompositionsSitesBySiteID,
  putSettingSavingEnergy,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {
  invertComma,
  numberWithPeriod,
  initialUpper,
} from '../../functions/convertString';
import {iconBlack} from '../../define';
import {useTranslation} from 'react-i18next';
import {PrimaryButton, SecondaryButton} from '../../component/BasicButton';

type loaderReturn = {
  /** 省エネ設定 */
  savingEnergy: apiIF.res.getSettingSavingEnergy;
  /** 構成情報 */
  compositions: {ouID: string; iuList: string[]}[];
  /** タイムゾーン */
  timeZone: apiIF.res.getCompositionsSitesBySiteID['timezone'];
};
export async function loader({
  params,
}: LoaderFunctionArgs): Promise<loaderReturn | undefined> {
  //パスパラメータを取得できない場合
  if (params.siteID === undefined) {
    return undefined;
  }
  const promises = [];
  //省エネ設定を取得
  promises.push(getSettingSavingEnergy(params.siteID));
  //ゲートウェイの情報を取得
  promises.push(
    getCompositionsGateway(params.siteID, {
      'fields': 'gateway-name,compositions',
      'max-result': 10,
    })
  );
  //サイトの情報を取得
  promises.push(getCompositionsSitesBySiteID(params.siteID));

  try {
    const result = await Promise.all(promises);
    const savingEnergy = result[0] as apiIF.res.getSettingSavingEnergy;
    const compositions = result[1] as apiIF.res.getCompositionsGateway;
    const siteInfo = result[2] as apiIF.res.getCompositionsSitesBySiteID;

    /** 室外機IDとその室外機に登録されている室内機の名前一覧 */
    const ouIDListWithIUName: loaderReturn['compositions'] = [];
    for (const gwInfo of savingEnergy['target-unit']) {
      for (const ouInfo of gwInfo['outdoor-unit']) {
        const [, gwID] = ouInfo['outdoor-id'].split('+');
        //prettier-ignore
        /** ゲートウェイ単位の構成情報 */
        const compositionGW = compositions['gateway-informations'].find(gwInfo => {
          return gwInfo['gateway-id'] === gwID;
        });
        if (
          compositionGW === undefined ||
          compositionGW.compositions === undefined
        ) {
          ouIDListWithIUName.push({ouID: ouInfo['outdoor-id'], iuList: []});
          continue;
        }

        //prettier-ignore
        /** SL1系統の室外機単位の構成情報 */
        const compositionSL1OU = compositionGW.compositions['sl-system-1']['sl-unit'].find(value => {
          return value['outdoor-id'] === ouInfo['outdoor-id'];
        });
        if (compositionSL1OU !== undefined) {
          ouIDListWithIUName.push({
            ouID: ouInfo['outdoor-id'],
            iuList: compositionSL1OU['indoor-unit'].map(iuInfo => {
              return iuInfo['indoor-unit-name'];
            }),
          });
          continue;
        }
        // prettier-ignore
        /** SL2系統の室外機単位の構成情報 */
        const compositionSL2OU = compositionGW.compositions['sl-system-2']['sl-unit'].find(value => {
          return value['outdoor-id'] === ouInfo['outdoor-id'];
        });
        if (compositionSL2OU === undefined) {
          ouIDListWithIUName.push({ouID: ouInfo['outdoor-id'], iuList: []});
          continue;
        }
        ouIDListWithIUName.push({
          ouID: ouInfo['outdoor-id'],
          iuList: compositionSL2OU['indoor-unit'].map(iuInfo => {
            return iuInfo['indoor-unit-name'];
          }),
        });
      }
    }

    return {
      savingEnergy: savingEnergy,
      compositions: ouIDListWithIUName,
      timeZone: siteInfo.timezone,
    };
  } catch {
    return undefined;
  }
}

type targetOUType = {
  gwID: apiIF.resParameters.savingEnergyTargetUnit['gateway-id'];
  gwName: apiIF.resParameters.savingEnergyTargetUnit['gateway-name'];
  ouID: apiIF.resParameters.savingEnergyTargetUnit['outdoor-unit'][0]['outdoor-id'];
  ouName: apiIF.resParameters.savingEnergyTargetUnit['outdoor-unit'][0]['outdoor-unit-name'];
  control: apiIF.resParameters.savingEnergyTargetUnit['outdoor-unit'][0]['control'];
}[];

/**
 * 省エネ制御設定詳細
 * @returns JSX.Element
 */
const SavingEnergyDetail = () => {
  const loaderData = useLoaderData() as loaderReturn | undefined;
  const {siteID} = useParams();
  const trans = useTranslation().t; //翻訳関数
  const navigate = useNavigate();

  /** 月の名前（参照用） */
  const monthFullTable: (keyof apiIF.res.getSettingSavingEnergy['season-setting'])[] =
    [
      'january',
      'february',
      'march',
      'april',
      'may',
      'june',
      'july',
      'august',
      'september',
      'october',
      'november',
      'december',
    ];
  /**
   * 修正前のシーズン設定 月ごとの冷暖設定用スライダーの範囲
   * @returns スライダーの範囲 [左端(number), 右端(number)]
   */
  const seasonRangeSetting = () => {
    if (loaderData === undefined) {
      return [];
    }

    // 冷房と暖房の境目を確認して後の月を保存する。
    const tempArray: number[] = [];
    for (let i = 0; i < monthFullTable.length - 1; i++) {
      if (
        loaderData.savingEnergy['season-setting'][monthFullTable[i]] !==
        loaderData.savingEnergy['season-setting'][monthFullTable[i + 1]]
      ) {
        tempArray.push(i + 2);
      }
      if (tempArray.length === 2) {
        break;
      }
    }

    if (tempArray.length === 0) {
      // 境目がない場合 例: 1月 ~ 12月 冷房
      tempArray.push(1, 12);
    } else if (tempArray.length === 1) {
      // 境目が一つの場合: 1月 ~ 6月 冷房 / 7月 ~ 12月 暖房
      tempArray.push(12);
    } else {
      // 境目が二つの場合: 1月 ~ 4月 冷房 / 5月 ~ 8月 暖房 / 9月 ~ 12月 冷房
      // 境目の内側にスライダーを設定するので2つ目を-1で調整する。
      tempArray[1]--;
    }
    return tempArray;
  };

  //サイトのタイムゾーン
  const [timeZone, setTimeZone] = useState<loaderReturn['timeZone']>('');
  //prettier-ignore
  //制御有効/無効設定
  const [control, setControl] = useState<loaderReturn['savingEnergy']['control']>('inactive');
  //prettier-ignore
  //目標消費電力量
  const [targetPower, setTargetPower] = useState<loaderReturn['savingEnergy']['target-power-consumption']>('');
  //月別係数
  const [monthlyCoefficient, setMonthlyCoefficient] = useState<number[]>([]);
  //prettier-ignore
  //月別目標消費電力量
  const [monthlyPower, setMonthlyPower] = useState<string[]>([...Array(12)].fill('0.0'));
  //prettier-ignore
  //日別係数（平日）
  const [weekdayCoefficient, setWeekdayCoefficient] = useState<loaderReturn['savingEnergy']['daily-coefficient']['weekday']>('');
  //prettier-ignore
  //日別係数（休日）
  const [holidayCoefficient, setHolidayCoefficient] = useState<loaderReturn['savingEnergy']['daily-coefficient']['holiday']>('');
  //prettier-ignore
  //日別係数（特定日1）
  const [sp1Coefficient, setSP1Coefficient] = useState<loaderReturn['savingEnergy']['daily-coefficient']['special-day-1']>('');
  //prettier-ignore
  //日別係数（特定日2）
  const [sp2Coefficient, setSP2Coefficient] = useState<loaderReturn['savingEnergy']['daily-coefficient']['special-day-2']>('');
  //prettier-ignore
  //制御を中止する温度差
  const [interruptTemp, setInterruptTemp] = useState<loaderReturn['savingEnergy']['interrupt-condition']>('');
  //prettier-ignore
  //シーズン設定の設定できる範囲の種類
  const [seasonRangeType, setSeasonRangeType] = useState<'cooling' | 'heating'>('cooling');
  //シーズン設定
  const [seasonRange, setSeasonRange] = useState(seasonRangeSetting());
  //制御対象の室外機
  const [targetOU, setTargetOU] = useState<targetOUType>([]);
  //保存ダイアログ表示フラグ
  const [isOpenApplyDialog, setIsOpenApplyDialog] = useState(false);
  const [targetPowerError, setTargetPowerError] = useState(false); //目標消費電力
  const [dailyCoefficientError, setDailyCoefficientError] = useState(false); //日別係数

  // ローダーのデータを変数に入れる。
  useEffect(() => {
    if (loaderData === undefined) {
      return;
    }

    setTimeZone(loaderData.timeZone);
    setControl(loaderData.savingEnergy.control);
    setTargetPower(loaderData.savingEnergy['target-power-consumption']);
    setMonthlyCoefficient([
      Number(loaderData.savingEnergy['monthly-coefficient'].january),
      Number(loaderData.savingEnergy['monthly-coefficient'].february),
      Number(loaderData.savingEnergy['monthly-coefficient'].march),
      Number(loaderData.savingEnergy['monthly-coefficient'].april),
      Number(loaderData.savingEnergy['monthly-coefficient'].may),
      Number(loaderData.savingEnergy['monthly-coefficient'].june),
      Number(loaderData.savingEnergy['monthly-coefficient'].july),
      Number(loaderData.savingEnergy['monthly-coefficient'].august),
      Number(loaderData.savingEnergy['monthly-coefficient'].september),
      Number(loaderData.savingEnergy['monthly-coefficient'].october),
      Number(loaderData.savingEnergy['monthly-coefficient'].november),
      Number(loaderData.savingEnergy['monthly-coefficient'].december),
    ]);
    setWeekdayCoefficient(
      loaderData.savingEnergy['daily-coefficient']['weekday']
    );
    setHolidayCoefficient(
      loaderData.savingEnergy['daily-coefficient']['holiday']
    );
    setSP1Coefficient(
      loaderData.savingEnergy['daily-coefficient']['special-day-1']
    );
    setSP2Coefficient(
      loaderData.savingEnergy['daily-coefficient']['special-day-2']
    );
    setInterruptTemp(loaderData.savingEnergy['interrupt-condition']);
    const seasonRange = seasonRangeSetting();
    setSeasonRangeType(
      loaderData.savingEnergy['season-setting'][
        monthFullTable[seasonRange[0] - 1]
      ]
    );
    const temp: targetOUType = [];
    for (const gwInfo of loaderData.savingEnergy['target-unit']) {
      for (const ouInfo of gwInfo['outdoor-unit']) {
        temp.push({
          gwID: gwInfo['gateway-id'],
          gwName: gwInfo['gateway-name'],
          ouID: ouInfo['outdoor-id'],
          ouName: ouInfo['outdoor-unit-name'],
          control: ouInfo['control'],
        });
      }
    }
    setTargetOU(temp);
  }, [loaderData]);

  //月別目標消費電力量の更新
  useEffect(() => {
    const targetPowerNumber = Number(targetPower);
    if (targetPowerNumber <= 0) return;

    /** 月別係数の合計 */
    const sum = monthlyCoefficient.reduce((result, coefficient) => {
      result += coefficient;
      return result;
    }, 0);
    if (sum === 0) {
      setMonthlyPower([...Array(12)].fill('0.0'));
    } else {
      setMonthlyPower(
        monthlyCoefficient.map(coefficient => {
          // 年間目標消費電力 * (月別係数 / 月別係数の合計)
          const power = targetPowerNumber * (coefficient / sum);
          return power.toFixed(1);
        })
      );
    }
  }, [targetPower, monthlyCoefficient]);

  /**
   * 月別冷暖設定
   * @param month 月
   * @returns 冷暖設定
   */
  const monthlyCoefficientSetting = (month: number) => {
    if (seasonRangeType === 'cooling') {
      return month >= seasonRange[0] && month <= seasonRange[1]
        ? 'cooling'
        : 'heating';
    } else if (seasonRangeType === 'heating') {
      return month >= seasonRange[0] && month <= seasonRange[1]
        ? 'heating'
        : 'cooling';
    }
    return 'cooling';
  };

  /**
   * 保存処理実行時
   */
  const applyOKButtonOnClick = async () => {
    if (siteID === undefined) {
      return;
    }

    //Apiの形に整形する
    const targetOUForRequestBody: apiIF.reqParameters.putSettingSavingEnergy['target-outdoor-unit'] =
      targetOU.map(unit => {
        return {
          'outdoor-id': unit.ouID,
          'control': unit.control,
        };
      });
    const requestBody: apiIF.reqParameters.putSettingSavingEnergy = {
      'control': control,
      'target-power-consumption': targetPower,
      'monthly-coefficient': {
        january: monthlyCoefficient[0].toFixed(1),
        february: monthlyCoefficient[1].toFixed(1),
        march: monthlyCoefficient[2].toFixed(1),
        april: monthlyCoefficient[3].toFixed(1),
        may: monthlyCoefficient[4].toFixed(1),
        june: monthlyCoefficient[5].toFixed(1),
        july: monthlyCoefficient[6].toFixed(1),
        august: monthlyCoefficient[7].toFixed(1),
        september: monthlyCoefficient[8].toFixed(1),
        october: monthlyCoefficient[9].toFixed(1),
        november: monthlyCoefficient[10].toFixed(1),
        december: monthlyCoefficient[11].toFixed(1),
      },
      'daily-coefficient': {
        'holiday': holidayCoefficient,
        'special-day-1': sp1Coefficient,
        'special-day-2': sp2Coefficient,
      },
      'interrupt-condition': interruptTemp.toString(),
      'season-setting': {
        january: monthlyCoefficientSetting(1),
        february: monthlyCoefficientSetting(2),
        march: monthlyCoefficientSetting(3),
        april: monthlyCoefficientSetting(4),
        may: monthlyCoefficientSetting(5),
        june: monthlyCoefficientSetting(6),
        july: monthlyCoefficientSetting(7),
        august: monthlyCoefficientSetting(8),
        september: monthlyCoefficientSetting(9),
        october: monthlyCoefficientSetting(10),
        november: monthlyCoefficientSetting(11),
        december: monthlyCoefficientSetting(12),
      },
      'target-outdoor-unit': targetOUForRequestBody,
    };

    try {
      await putSettingSavingEnergy(siteID, requestBody);
      return 'OK';
    } catch (errorMessage) {
      setIsOpenApplyDialog(false);
      throw errorMessage;
    }
  };

  return (
    <Container fixed id={`mv-savingEnergySetting`}>
      <Stack spacing={3}>
        {/** タイトルと説明 */}
        <Box className="savingEnergyDetail__explanation">
          <h1>{trans('SavingEnergy.title')}</h1>
          <h2>
            {trans('SavingEnergy.explanation', {
              active: trans('SavingEnergy.startSaving.active'),
            })}
          </h2>
        </Box>
        {/** 設定項目 */}
        <Box className="savingEnergyDetail__setting">
          <Stack spacing={3}>
            {/** 有効無効設定 */}
            {loaderData !== undefined && (
              <StartSaving
                control={control}
                setControl={setControl}
                startDate={loaderData.savingEnergy['start-date']}
                timeZone={timeZone}
                targetPowerError={targetPowerError}
              />
            )}
            {/** 年間目標消費電力 */}
            <TargetPowerConsumption
              control={control}
              targetPower={targetPower}
              setTargetPower={setTargetPower}
              setError={setTargetPowerError}
            />
            {/** シーズン設定、月別係数 */}
            <SeasonAndMonthlyCoefficient
              control={control}
              seasonRangeType={seasonRangeType}
              setSeasonRangeType={setSeasonRangeType}
              seasonRange={seasonRange}
              setSeasonRange={setSeasonRange}
              monthlyCoefficient={monthlyCoefficient}
              setMonthlyCoefficient={setMonthlyCoefficient}
              monthlyPower={monthlyPower}
              targetPowerError={targetPowerError}
            />
            {/** 日別係数 */}
            <DailyCoefficient
              control={control}
              weekdayCoefficient={weekdayCoefficient}
              holidayCoefficient={holidayCoefficient}
              setHolidayCoefficient={setHolidayCoefficient}
              sp1Coefficient={sp1Coefficient}
              setSP1Coefficient={setSP1Coefficient}
              sp2Coefficient={sp2Coefficient}
              setSP2Coefficient={setSP2Coefficient}
              setError={setDailyCoefficientError}
            />
            {/** 制御を中止する温度差 */}
            <TemperatureDifference
              control={control}
              temperature={interruptTemp}
              setTemperature={setInterruptTemp}
            />
            {/** 省エネ制御の指令対象 */}
            <TargetOfEnergySaving
              control={control}
              targetOU={targetOU}
              setTargetOU={setTargetOU}
            />
          </Stack>
        </Box>
        {/** リセットボタン、保存ボタン */}
        <Box>
          <Grid container justifyContent="flex-end" columnSpacing={2}>
            <Grid item xs="auto">
              <SecondaryButton
                onClick={() => {
                  navigate(
                    `/pages/my-site/site-detail/${siteID}/saving-energy`
                  );
                }}
                disabled={control === 'inactive'}
                buttonText={trans('SavingEnergy.actions.cancel')}
              />
            </Grid>
            <Grid item xs="auto">
              <PrimaryButton
                onClick={() => {
                  setIsOpenApplyDialog(true);
                }}
                disabled={targetPowerError || dailyCoefficientError}
                buttonText={trans('SavingEnergy.actions.apply')}
              />
            </Grid>
          </Grid>
        </Box>
      </Stack>
      {/* 保存ボタン押下時、ダイアログで実行の最終確認をする */}
      <DialogPage
        open={isOpenApplyDialog}
        setOpen={setIsOpenApplyDialog}
        titleString={trans('SavingEnergy.actions.apply')}
        contentText={trans('SavingEnergy.actions.applyText')}
        buttonNameLeft={trans('SavingEnergy.actions.cancel')}
        buttonNameRight={trans('SavingEnergy.actions.ok')}
        execFunc={applyOKButtonOnClick}
        redirectPath={`/pages/my-site/site-detail/${siteID}/saving-energy`}
      />
    </Container>
  );
};

/**
 * 有効無効設定
 * @param param0 param0
 * @returns JSX.Element
 */
const StartSaving = ({
  control,
  setControl,
  startDate,
  timeZone,
  targetPowerError,
}: {
  /** 制御有効/無効設定 */
  control: loaderReturn['savingEnergy']['control'];
  /** 制御有効/無効設定（set関数） */
  setControl: React.Dispatch<
    React.SetStateAction<loaderReturn['savingEnergy']['control']>
  >;
  /** 省エネ制御開始（予定）日 */
  startDate: loaderReturn['savingEnergy']['start-date'];
  /** サイトのタイムゾーン */
  timeZone: loaderReturn['timeZone'];
  /** 目標消費電力でエラーが発生しているかどうか */
  targetPowerError: boolean;
}) => {
  const trans = useTranslation().t; //翻訳関数

  /**
   * 制御有効/無効設定変更時
   * @param event
   */
  const controlHandleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setControl('active');
    } else {
      setControl('inactive');
    }
  };
  /**
   * 省エネ開始日を返す
   * @returns 省エネ開始日
   */
  const getStartDate = () => {
    const date = utcToZonedTime(startDate, timeZone);
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();

    return trans('SavingEnergy.startSaving.date', {
      day: day,
      month: month,
      year: year,
    });
  };

  return (
    <Box>
      <Grid container columnSpacing={2} alignItems="center">
        {/** 切替スイッチ */}
        <Grid item xs="auto" container alignItems="center">
          <Grid item xs="auto">
            {trans('SavingEnergy.startSaving.inactive')}
          </Grid>
          <Grid item xs="auto">
            <Switch
              checked={control === 'active'}
              onChange={controlHandleChange}
              disabled={targetPowerError}
            />
          </Grid>
          <Grid item xs="auto">
            {trans('SavingEnergy.startSaving.active')}
          </Grid>
        </Grid>
        {/** 開始（予定）日 */}
        <Grid item xs="auto">
          {trans('SavingEnergy.startSaving.title')}&nbsp;:&nbsp;
          {getStartDate()}
        </Grid>
      </Grid>
    </Box>
  );
};

/**
 * 年間目標消費電力
 * @param param0 param0
 * @returns JSX.Element
 */
const TargetPowerConsumption = ({
  control,
  targetPower,
  setTargetPower,
  setError,
}: {
  /** 制御有効/無効設定 */
  control: loaderReturn['savingEnergy']['control'];
  /** 目標消費電力量 */
  targetPower: loaderReturn['savingEnergy']['target-power-consumption'];
  /** 目標消費電力量（set関数） */
  setTargetPower: React.Dispatch<
    React.SetStateAction<
      loaderReturn['savingEnergy']['target-power-consumption']
    >
  >;
  /** 目標消費電力でエラーが発生しているかどうか（set関数） */
  setError: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const trans = useTranslation().t; //翻訳関数

  return (
    <Box>
      <Stack>
        {/** タイトル */}
        <Box className="savingEnergyDetail__setting__title">
          {trans('SavingEnergy.targetPowerConsumption.title')}
        </Box>
        {/** 入力欄 */}
        <Box className="savingEnergyDetail__setting__targetPower__textBox">
          <ChkInputTextbox
            id={`mv-yearTargetSavingEnergySetting`}
            autoFocus
            setValue={setTargetPower}
            setError={setError}
            chkInputTxtOK={/^[1-9]\d*$/}
            type="text"
            value={targetPower}
            helperText=" "
            errHelperText={trans(
              'SavingEnergy.targetPowerConsumption.incorrectEntry'
            )}
            variant="standard"
            disabled={control === 'inactive'}
            InputProps={{
              endAdornment: <InputAdornment position="end">kWh</InputAdornment>,
            }}
          />
        </Box>
        {/** 注意点 */}
        <Box>
          <List disablePadding>
            <ListItem
              disablePadding
              className="savingEnergyDetail__setting__targetPower__explanation--emphasis">
              <ListItemText
                primary={`- ${trans(
                  'SavingEnergy.targetPowerConsumption.explanation1.title'
                )} :`}
              />
            </ListItem>
            <List
              dense
              component="div"
              disablePadding
              className="savingEnergyDetail__setting__targetPower__explanation--emphasis">
              <ListItem>
                <ListItemText
                  primary={`1. ${trans(
                    'SavingEnergy.targetPowerConsumption.explanation1.step1',
                    {
                      inactive: trans('SavingEnergy.startSaving.inactive'),
                      buttonName: trans('SavingEnergy.actions.apply'),
                    }
                  )}`}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={`2. ${trans(
                    'SavingEnergy.targetPowerConsumption.explanation1.step2',
                    {
                      active: trans('SavingEnergy.startSaving.active'),
                      buttonName: trans('SavingEnergy.actions.apply'),
                    }
                  )}`}
                />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={trans(
                    'SavingEnergy.targetPowerConsumption.explanation1.caution'
                  )}
                />
              </ListItem>
            </List>
            <ListItem disablePadding>
              <ListItemText
                primary={`- ${trans(
                  'SavingEnergy.targetPowerConsumption.explanation2'
                )}`}
              />
            </ListItem>
          </List>
        </Box>
      </Stack>
    </Box>
  );
};

/**
 * シーズン設定、月別係数
 * @param param0 param0
 * @returns JSX.Element
 */
const SeasonAndMonthlyCoefficient = ({
  control,
  seasonRangeType,
  setSeasonRangeType,
  seasonRange,
  setSeasonRange,
  monthlyCoefficient,
  setMonthlyCoefficient,
  monthlyPower,
  targetPowerError,
}: {
  /** 制御有効/無効設定 */
  control: loaderReturn['savingEnergy']['control'];
  /** シーズン設定の設定できる範囲の種類 */
  seasonRangeType: 'cooling' | 'heating';
  /** シーズン設定の設定できる範囲の種類（set関数） */
  setSeasonRangeType: React.Dispatch<
    React.SetStateAction<'cooling' | 'heating'>
  >;
  /** シーズン設定 */
  seasonRange: number[];
  /** シーズン設定（set関数） */
  setSeasonRange: React.Dispatch<React.SetStateAction<number[]>>;
  /** 月別係数 */
  monthlyCoefficient: number[];
  /** 月別係数（set関数） */
  setMonthlyCoefficient: React.Dispatch<React.SetStateAction<number[]>>;
  /** 月別目標消費電力量 */
  monthlyPower: string[];
  /** 目標消費電力でエラーが発生しているかどうか */
  targetPowerError: boolean;
}) => {
  const trans = useTranslation().t; //翻訳関数

  /**
   * シーズン範囲変更時
   * @param event
   * @param type
   * @returns
   */
  const seasonRangeTypeHandleChange = (
    event: React.MouseEvent<HTMLElement>,
    type: 'cooling' | 'heating' | null
  ) => {
    if (type === null) return;
    setSeasonRangeType(type);
  };
  /**
   * シーズン設定変更時
   * @param event
   * @param newValue
   * @returns
   */
  const seasonSettingHandleChange = (
    event: Event,
    newValue: number | number[]
  ) => {
    if (Array.isArray(newValue) === false) return;
    setSeasonRange(newValue as number[]);
  };
  /**
   * 月別係数変更時
   * @param newValue 変更後の係数
   * @param month 変更した月
   */
  const monthlyCoefficientHandleChange = (
    newValue: number | number[],
    month: number
  ) => {
    if (Array.isArray(newValue)) return;

    setMonthlyCoefficient(
      monthlyCoefficient.map((coefficient, index) => {
        if (index + 1 === month) return newValue;
        return coefficient;
      })
    );
  };
  /**
   * 月別係数の算出
   */
  const calculationMonthlyCoefficient = () => {
    /** 中央値 */
    const median = (seasonRange[1] - seasonRange[0]) / 2 + seasonRange[0];

    /** 算出結果 */
    const calculationResult = monthlyCoefficient.map((_, index) => {
      /** 月 */
      const month = index + 1;
      /** 係数 */
      let coefficient = 0;
      if (month >= seasonRange[0] && month <= seasonRange[1]) {
        //12月と1月をまたいでいない区間の場合
        if (month < median) {
          coefficient = (month - seasonRange[0]) / 6;
        } else {
          coefficient = -(month - seasonRange[1]) / 6;
        }
      } else {
        //12月と1月をまたいでいる区間の場合
        /** 開始月 */
        const start = seasonRange[1] + 1;
        /** 終了月 */
        const end = seasonRange[0] - 1;
        if (median + 6 <= 12) {
          //中央値が12月以前の場合
          if (month >= start && month < median + 6) {
            coefficient = (month - start) / 6;
          } else {
            if (month >= start && month <= 12) {
              coefficient = -(month - 12 - end) / 6;
            } else {
              coefficient = -(month - end) / 6;
            }
          }
        } else {
          //中央値が1月以降の場合
          if (month < median + 6 - 12) {
            coefficient = (month + 12 - start) / 6;
          } else {
            if (month >= start && month <= 12) {
              coefficient = (month - start) / 6;
            } else {
              coefficient = -(month - end) / 6;
            }
          }
        }
      }

      //計算結果が0の場合は1に補正する
      if (coefficient <= 0) {
        return 1;
      }
      //有効数字は小数点以下2桁にし、10倍する
      coefficient = Number(coefficient.toFixed(2)) * 10;
      return coefficient;
    });
    setMonthlyCoefficient(calculationResult);
  };
  /**
   * 月表示変換テーブル
   * @param month 月
   * @returns 変換後の表示
   */
  const monthTable = (month: number) => {
    switch (month) {
      case 1:
        return trans('SavingEnergy.seasonSettings.january');
      case 2:
        return trans('SavingEnergy.seasonSettings.february');
      case 3:
        return trans('SavingEnergy.seasonSettings.march');
      case 4:
        return trans('SavingEnergy.seasonSettings.april');
      case 5:
        return trans('SavingEnergy.seasonSettings.may');
      case 6:
        return trans('SavingEnergy.seasonSettings.june');
      case 7:
        return trans('SavingEnergy.seasonSettings.july');
      case 8:
        return trans('SavingEnergy.seasonSettings.august');
      case 9:
        return trans('SavingEnergy.seasonSettings.september');
      case 10:
        return trans('SavingEnergy.seasonSettings.october');
      case 11:
        return trans('SavingEnergy.seasonSettings.november');
      case 12:
        return trans('SavingEnergy.seasonSettings.december');
      default:
        return '';
    }
  };
  /**
   * 月別の冷暖設定用スライダーのスタイル
   * @param month 月
   * @returns クラス名
   */
  const monthlyCoefficientSliderStyle = (month: number) => {
    if (seasonRangeType === 'cooling') {
      return month >= seasonRange[0] && month <= seasonRange[1]
        ? 'savingEnergyDetail__setting__monthlyCoefficient__sliderBox--coolingSeason'
        : 'savingEnergyDetail__setting__monthlyCoefficient__sliderBox--heatingSeason';
    } else if (seasonRangeType === 'heating') {
      return month >= seasonRange[0] && month <= seasonRange[1]
        ? 'savingEnergyDetail__setting__monthlyCoefficient__sliderBox--heatingSeason'
        : 'savingEnergyDetail__setting__monthlyCoefficient__sliderBox--coolingSeason';
    }
    return '';
  };

  return (
    <Box>
      <Stack spacing={1}>
        {/** タイトル */}
        <Box className="savingEnergyDetail__setting__title">
          {trans('SavingEnergy.seasonSettings.title')}
        </Box>
        {/** 説明 */}
        <Box className="savingEnergyDetail__setting__explanation">
          {trans('SavingEnergy.seasonSettings.explanation')}
        </Box>
        {/** やり方 */}
        <Box>
          <List dense>
            <ListItem disablePadding>
              <ListItemText
                primary={`1. ${trans(
                  'SavingEnergy.seasonSettings.operation.step1'
                )}`}
              />
            </ListItem>
            <ListItem disablePadding>
              <ListItemText
                primary={`2. ${trans(
                  'SavingEnergy.seasonSettings.operation.step2',
                  {
                    buttonName: trans('SavingEnergy.seasonSettings.button'),
                  }
                )}`}
              />
            </ListItem>
            <ListItem disablePadding>
              <ListItemText
                primary={`3. ${trans(
                  'SavingEnergy.seasonSettings.operation.step3'
                )}`}
              />
            </ListItem>
          </List>
        </Box>
        {/** 入力欄 */}
        <Box>
          <Grid container alignItems="flex-end" spacing={2}>
            {/** 冷暖反転ボタン */}
            <Grid item xs="auto">
              <ToggleButtonGroup
                exclusive
                onChange={seasonRangeTypeHandleChange}
                value={seasonRangeType}
                aria-label="Season range"
                disabled={control === 'inactive'}>
                <ToggleButton
                  value="cooling"
                  title={`${trans(
                    'SavingEnergy.seasonSettings.coolingTitle'
                  )}`}>
                  <img
                    src={iconBlack.COOLING.src}
                    alt={`${trans(
                      `SavingEnergy.image.alternativeText.${iconBlack.COOLING.alt}`
                    )}`}
                  />
                </ToggleButton>
                <ToggleButton
                  value="heating"
                  title={`${trans(
                    'SavingEnergy.seasonSettings.heatingTitle'
                  )}`}>
                  <img
                    src={iconBlack.HEATING.src}
                    alt={`${trans(
                      `SavingEnergy.image.alternativeText.${iconBlack.HEATING.alt}`
                    )}`}
                  />
                </ToggleButton>
              </ToggleButtonGroup>
            </Grid>
            {/** 月別係数計算ボタン */}
            <Grid item xs>
              <Button
                variant="outlined"
                startIcon={<CalculateIcon />}
                color="inherit"
                disabled={targetPowerError || control === 'inactive'}
                size="large"
                onClick={calculationMonthlyCoefficient}>
                {trans('SavingEnergy.seasonSettings.button')}
              </Button>
            </Grid>
            {/** シーズン設定 */}
            <Grid item xs={12} container justifyContent="flex-end">
              <Grid item xs={12} lg={(12 / 13) * 12}>
                <Box
                  className={`${
                    seasonRangeType === 'cooling'
                      ? 'savingEnergyDetail__setting__seasonSetting--coolingRange'
                      : 'savingEnergyDetail__setting__seasonSetting--heatingRange'
                  } savingEnergyDetail__setting__seasonSetting__slider`}>
                  <Slider
                    disableSwap
                    disabled={control === 'inactive'}
                    getAriaLabel={() =>
                      `${initialUpper(seasonRangeType)} range`
                    }
                    getAriaValueText={value => `${value} month`}
                    value={seasonRange}
                    onChange={seasonSettingHandleChange}
                    marks={[...Array(12)].map((_, index) => ({
                      value: index + 1,
                      label: (
                        <Box className="savingEnergyDetail__setting__seasonSetting__slider__label">
                          {monthTable(index + 1)}
                        </Box>
                      ),
                    }))}
                    min={1}
                    max={12}
                  />
                </Box>
              </Grid>
            </Grid>
            {/** 月別係数 */}
            <Grid item xs={12}>
              <Paper
                elevation={3}
                className="savingEnergyDetail__setting__monthlyCoefficient__sliderBox">
                <Grid container alignItems="flex-end">
                  {/** lgより小さい画面では非表示 */}
                  <Grid
                    item
                    lg={12 / 13}
                    sx={{display: {xs: 'none', lg: 'block'}}}
                    className="savingEnergyDetail__setting__monthlyCoefficient__power">
                    {trans('SavingEnergy.seasonSettings.consumption')}
                    &nbsp;(kWh)
                  </Grid>
                  {monthlyCoefficient.map((coefficient, index) => {
                    /** 月別目標消費電力量 */
                    const targetPower = invertComma(monthlyPower[index]);
                    /** スライダーに適用するクラス名 */
                    const sliderClassName = `savingEnergyDetail__setting__monthlyCoefficient__sliderBox__verticalSlider ${monthlyCoefficientSliderStyle(
                      index + 1
                    )}`;
                    return (
                      <Grid
                        item
                        xs={1}
                        lg={12 / 13}
                        key={`monthlyCoefficient${index}`}>
                        <Stack spacing={3} alignItems="center">
                          {/* 月ごとの冷暖設定用スライダー */}
                          <Box className={sliderClassName}>
                            <Slider
                              aria-label={`${index + 1} month coefficient`}
                              disabled={
                                targetPowerError || control === 'inactive'
                              }
                              orientation="vertical"
                              value={coefficient}
                              onChange={(
                                event: Event,
                                newValue: number | number[]
                              ) => {
                                monthlyCoefficientHandleChange(
                                  newValue,
                                  index + 1
                                );
                              }}
                              valueLabelDisplay="off"
                              min={1}
                              max={10}
                              step={0.1}
                              marks={[
                                {value: 1},
                                {value: 3.25},
                                {value: 5.5},
                                {value: 7.75},
                                {value: 10},
                              ]}
                            />
                          </Box>
                          {/** 月間目標消費電力 */}
                          <Box>
                            {targetPower === undefined
                              ? ''
                              : numberWithPeriod(targetPower)}
                          </Box>
                        </Stack>
                      </Grid>
                    );
                  })}
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Stack>
    </Box>
  );
};

/**
 * 日別係数
 * @param param0 param0
 * @returns JSX.Element
 */
const DailyCoefficient = ({
  control,
  weekdayCoefficient,
  holidayCoefficient,
  setHolidayCoefficient,
  sp1Coefficient,
  setSP1Coefficient,
  sp2Coefficient,
  setSP2Coefficient,
  setError,
}: {
  /** 制御有効/無効設定 */
  control: loaderReturn['savingEnergy']['control'];
  /** 日別係数（平日） */
  weekdayCoefficient: loaderReturn['savingEnergy']['daily-coefficient']['weekday'];
  /** 日別係数（休日） */
  holidayCoefficient: loaderReturn['savingEnergy']['daily-coefficient']['holiday'];
  /** 日別係数（休日）（set関数） */
  setHolidayCoefficient: React.Dispatch<
    React.SetStateAction<
      loaderReturn['savingEnergy']['daily-coefficient']['holiday']
    >
  >;
  /** 日別係数（特定日1） */
  sp1Coefficient: loaderReturn['savingEnergy']['daily-coefficient']['special-day-1'];
  /** 日別係数（特定日1）（set関数） */
  setSP1Coefficient: React.Dispatch<
    React.SetStateAction<
      loaderReturn['savingEnergy']['daily-coefficient']['special-day-1']
    >
  >;
  /** 日別係数（特定日2） */
  sp2Coefficient: loaderReturn['savingEnergy']['daily-coefficient']['special-day-2'];
  /** 日別係数（特定日2）（set関数） */
  setSP2Coefficient: React.Dispatch<
    React.SetStateAction<
      loaderReturn['savingEnergy']['daily-coefficient']['special-day-2']
    >
  >;
  /** 日別係数でエラーが発生しているかどうか（set関数） */
  setError: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const trans = useTranslation().t; //翻訳関数

  return (
    <Box>
      <Stack spacing={3}>
        {/** タイトル */}
        <Box className="savingEnergyDetail__setting__title">
          {trans('SavingEnergy.dailyCoefficient.title')}
        </Box>
        {/** 説明 */}
        <Box className="savingEnergyDetail__setting__explanation">
          {trans('SavingEnergy.dailyCoefficient.explanation')}
        </Box>
        {/** 入力欄 */}
        <Grid container>
          {/** 平日 */}
          <Grid item xs={2}>
            <Stack spacing={1}>
              <Box>{trans('SavingEnergy.dailyCoefficient.weekday')}</Box>
              <Box>{weekdayCoefficient}&nbsp;%</Box>
            </Stack>
          </Grid>
          {/** 休日 */}
          <Grid item xs={2}>
            <Stack>
              <Box>{trans('SavingEnergy.dailyCoefficient.holiday')}</Box>
              <Box className="savingEnergyDetail__setting__dailyCoefficient__textBox">
                <ChkInputTextbox
                  setValue={setHolidayCoefficient}
                  setError={setError}
                  chkInputTxtOK={/^\d$|^[1-9]\d$|^[1-9]\d\d$|^1000$/}
                  type="text"
                  value={holidayCoefficient}
                  helperText=" "
                  errHelperText={trans(
                    'SavingEnergy.dailyCoefficient.incorrectEntry'
                  )}
                  variant="standard"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                  }}
                  disabled={control === 'inactive'}
                />
              </Box>
            </Stack>
          </Grid>
          {/** 特別日１ */}
          <Grid item xs={2}>
            <Stack>
              <Box>{trans('SavingEnergy.dailyCoefficient.specialDay1')}</Box>
              <Box className="savingEnergyDetail__setting__dailyCoefficient__textBox">
                <ChkInputTextbox
                  setValue={setSP1Coefficient}
                  setError={setError}
                  chkInputTxtOK={/^\d$|^[1-9]\d$|^[1-9]\d\d$|^1000$/}
                  type="text"
                  value={sp1Coefficient}
                  helperText=" "
                  errHelperText={trans(
                    'SavingEnergy.dailyCoefficient.incorrectEntry'
                  )}
                  variant="standard"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                  }}
                  disabled={control === 'inactive'}
                />
              </Box>
            </Stack>
          </Grid>
          {/** 特別日２ */}
          <Grid item xs={2}>
            <Stack>
              <Box>{trans('SavingEnergy.dailyCoefficient.specialDay2')}</Box>
              <Box className="savingEnergyDetail__setting__dailyCoefficient__textBox">
                <ChkInputTextbox
                  setValue={setSP2Coefficient}
                  setError={setError}
                  chkInputTxtOK={/^\d$|^[1-9]\d$|^[1-9]\d\d$|^1000$/}
                  type="text"
                  value={sp2Coefficient}
                  helperText=" "
                  errHelperText={trans(
                    'SavingEnergy.dailyCoefficient.incorrectEntry'
                  )}
                  variant="standard"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">%</InputAdornment>
                    ),
                  }}
                  disabled={control === 'inactive'}
                />
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  );
};

/**
 * 制御を中止する温度差
 * @param param0 param0
 * @returns JSX.Element
 */
const TemperatureDifference = ({
  control,
  temperature,
  setTemperature,
}: {
  /** 制御有効/無効設定 */
  control: loaderReturn['savingEnergy']['control'];
  /** 制御を中止する温度差 */
  temperature: loaderReturn['savingEnergy']['interrupt-condition'];
  /** 制御を中止する温度差（set関数） */
  setTemperature: React.Dispatch<
    React.SetStateAction<loaderReturn['savingEnergy']['interrupt-condition']>
  >;
}) => {
  const trans = useTranslation().t; //翻訳関数

  /**
   * 制御中止温度変更時
   * @param event
   * @param newValue
   */
  const temperatureHandleChange = (
    event: Event,
    newValue: number | number[]
  ) => {
    if (Array.isArray(newValue)) {
      setTemperature(newValue[newValue.length - 1].toString());
    } else {
      setTemperature(newValue.toString());
    }
  };

  return (
    <Box>
      <Stack spacing={2}>
        {/** タイトル */}
        <Box className="savingEnergyDetail__setting__title">
          {trans('SavingEnergy.temperatureDifference.title')}
        </Box>
        {/** 説明その１ */}
        <Box className="savingEnergyDetail__setting__explanation">
          {trans('SavingEnergy.temperatureDifference.explanation')}
        </Box>
        {/** 説明その２ */}
        <Box>
          {trans('SavingEnergy.temperatureDifference.coolingExplanation')}
          <br />
          {trans('SavingEnergy.temperatureDifference.heatingExplanation')}
        </Box>
        {/** 入力欄 */}
        <Box
          className={
            'savingEnergyDetail__setting__stopTemp__temperatureDifferenceSlider'
          }>
          <Slider
            aria-label="Interrupt condition"
            disabled={control === 'inactive'}
            getAriaValueText={value => `${value} °C`}
            value={Number(temperature)}
            onChange={temperatureHandleChange}
            marks={[
              {value: 1, label: '1 °C'},
              {value: 2, label: '2 °C'},
              {value: 3, label: '3 °C'},
              {value: 4, label: '4 °C'},
              {value: 5, label: '5 °C'},
              {value: 6, label: '6 °C'},
              {value: 7, label: '7 °C'},
              {value: 8, label: '8 °C'},
              {value: 9, label: '9 °C'},
              {value: 10, label: '10 °C'},
            ]}
            min={1}
            max={10}
          />
        </Box>
      </Stack>
    </Box>
  );
};

/**
 * 省エネ制御の指令対象
 * @param param0 param0
 * @returns JSX.Element
 */
const TargetOfEnergySaving = ({
  control,
  targetOU,
  setTargetOU,
}: {
  /** 制御有効/無効設定 */
  control: loaderReturn['savingEnergy']['control'];
  /** 制御対象の室外機 */
  targetOU: targetOUType;
  /** 制御対象の室外機（set関数） */
  setTargetOU: React.Dispatch<React.SetStateAction<targetOUType>>;
}) => {
  const trans = useTranslation().t; //翻訳関数

  /**
   * 制御対象「all」チェックボックス押下時
   * @param checked すべての室外機がチェック状態の場合はtrue
   */
  const targetUnitAllHandleChange = (checked: boolean) => () => {
    if (checked) {
      setTargetOU(
        targetOU.map(gw => {
          return {
            ...gw,
            control: 'inactive',
          };
        })
      );
    } else {
      setTargetOU(
        targetOU.map(gw => {
          return {
            ...gw,
            control: 'active',
          };
        })
      );
    }
  };
  /**
   * 制御対象室外機チェックボックス押下時
   * @param ouID 押下した室外機のID
   */
  const targetUnitHandleChange = (ouID: string) => () => {
    setTargetOU(
      targetOU.map(unit => {
        if (unit.ouID === ouID) {
          return {
            ...unit,
            control: unit.control === 'active' ? 'inactive' : 'active',
          };
        }
        return unit;
      })
    );
  };

  return (
    <Box>
      <Stack>
        {/** タイトル */}
        <Box className="savingEnergyDetail__setting__title">
          {trans('SavingEnergy.targetOfEnergySaving.title')}
        </Box>
        {/** 説明 */}
        <Box className="savingEnergyDetail__setting__explanation">
          {trans('SavingEnergy.targetOfEnergySaving.explanation')}
        </Box>
        {/** 注意 */}
        <Box className="savingEnergyDetail__setting__targetPower__explanation--emphasis">
          <List>
            <ListItem>
              <ListItemText
                primary={`- ${trans(
                  'SavingEnergy.targetOfEnergySaving.caution'
                )}`}
              />
            </ListItem>
          </List>
        </Box>
        {/** 入力欄 */}
        <Paper>
          <List
            disablePadding
            className="savingEnergyDetail__setting__targetUnit">
            <ListItem>
              <ListItemButton
                disabled={control === 'inactive'}
                onClick={targetUnitAllHandleChange(
                  targetOU.every(gw => gw.control === 'active')
                )}>
                <ListItemIcon>
                  <Checkbox
                    checked={targetOU.every(gw => gw.control === 'active')}
                    indeterminate={
                      targetOU.some(gw => gw.control === 'active') &&
                      targetOU.some(gw => gw.control === 'inactive')
                    }
                  />
                </ListItemIcon>
                <ListItemText
                  primary={trans('SavingEnergy.targetOfEnergySaving.all')}
                />
              </ListItemButton>
            </ListItem>
            <List component="div" disablePadding>
              {targetOU.map((gw, index, array) => {
                return (
                  <Fragment key={gw.ouID}>
                    {(index === 0 || gw.gwID !== array[index - 1].gwID) && (
                      <ListSubheader inset>{gw.gwName}</ListSubheader>
                    )}
                    <ListItem
                      disablePadding
                      className="savingEnergyDetail__setting__targetUnit__item">
                      <ListItemButton
                        disabled={control === 'inactive'}
                        onClick={targetUnitHandleChange(gw['ouID'])}>
                        <ListItemIcon>
                          <Checkbox checked={gw.control === 'active'} />
                        </ListItemIcon>
                        <ListItemText primary={gw['ouName']} />
                      </ListItemButton>
                    </ListItem>
                  </Fragment>
                );
              })}
            </List>
          </List>
        </Paper>
      </Stack>
    </Box>
  );
};

export default SavingEnergyDetail;
